/** Logistics helpers shared by orders, order detail, returns and shipping settings. */
import React, { useEffect, useState } from 'react';
import { Select, Tag, message } from 'antd';
import { t as tt, useTranslation } from '../../../shared/i18n';
import apiClient from '../../../shared/services/apiClient';
import { BASE, QUIET } from './api';
import { listOf } from './common';

let carriersCache = null;
/** GET carriers (cached per page load) → [{code, name, tracking_url_template}] */
export const useCarriers = (api) => {
    const [list, setList] = useState(carriersCache || []);
    useEffect(() => {
        if (carriersCache) return;
        api.get('/carriers', null, QUIET).then((d) => { carriersCache = listOf(d); setList(carriersCache); }).catch(() => {});
    }, [api]);
    return list;
};

export const CarrierSelect = ({ carriers, ...rest }) => {
    const { t } = useTranslation();
    return (
        <Select showSearch allowClear optionFilterProp="label" placeholder={t('Carrier')} {...rest}
            options={carriers.map((c) => ({ value: c.code, label: c.code === 'other' ? t('Other') : c.name }))} />
    );
};

/**
 * Open a printable HTML document (packing slip / invoice) from the API in a new window.
 * The API needs the auth header, so the HTML is fetched here and shown from a Blob URL.
 */
export const openDocument = async (siteId, path, params) => {
    const w = window.open('', '_blank');
    try {
        const res = await apiClient.get(`${BASE}/${siteId}${path}`, { params, responseType: 'text', transformResponse: (x) => x });
        const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        if (w) w.location.href = url; else window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
        if (w) w.close();
        if (!e?.toasted) message.error(tt('Could not open the document'));
    }
};

const TRACK = {
    pre_transit: ['default', 'Label created'], in_transit: ['blue', 'In transit'], out_for_delivery: ['cyan', 'Out for delivery'],
    delivered: ['green', 'Delivered'], exception: ['red', 'Delivery exception'],
};
export const TrackingTag = ({ value }) => {
    const { t } = useTranslation();
    if (!value) return null;
    const [c, l] = TRACK[value] || ['default', value];
    return <Tag color={c}>{t(l, { context: 'return' })}</Tag>;
};

const RET = {
    requested: ['gold', 'Requested'], approved: ['blue', 'Approved'], rejected: ['red', 'Rejected'], received: ['cyan', 'Received'],
    refunded: ['purple', 'Refunded'], closed: ['default', 'Closed'],
};
export const RETURN_STATUSES = Object.keys(RET);
export const ReturnTag = ({ value }) => {
    const { t } = useTranslation();
    if (!value) return null;
    const [c, l] = RET[value] || ['default', value];
    return <Tag color={c}>{t(l, { context: 'return' })}</Tag>;
};
